import React from 'react';
import { RescueChipHistoryEntry } from '../../lib/chipAdapters';
import { formatDate } from '../../lib/utils';
import { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Button from '../../components/Button/Button';
import styles from './ChipHistoryTimeline.module.scss';

interface ChipHistoryTimelineProps {
  history: RescueChipHistoryEntry[];
  chipLabel: string;
  chipId: string;
  onBack: () => void;
}

const EVENT_LABELS: Record<RescueChipHistoryEntry['event'], string> = {
  registered: 'Registered',
  owner_added: 'Owner Added',
  owner_cleared: 'Owner Cleared',
  transferred_to_org: 'Transferred to Org',
  reclaimed: 'Reclaimed',
};

const EVENT_BADGE_VARIANT: Record<
  RescueChipHistoryEntry['event'],
  'success' | 'error' | 'info' | 'warning' | 'neutral'
> = {
  registered: 'info',
  owner_added: 'success',
  owner_cleared: 'warning',
  transferred_to_org: 'neutral',
  reclaimed: 'error',
};

export default function ChipHistoryTimeline({ history, chipLabel, chipId, onBack }: ChipHistoryTimelineProps) {
  return (
    <>
      <ModalTitle>History &mdash; {chipLabel}</ModalTitle>

      <div className={styles.meta}>
        <div>
          <div className={styles.metaLabel}>CHIP ID</div>
          <div className={styles.metaValue}>{chipId}</div>
        </div>
        <div>
          <div className={styles.metaLabel}>PET</div>
          <div className={styles.metaValue}>{chipLabel}</div>
        </div>
      </div>

      <ul className={styles.timeline}>
        {history.map((entry, index) => {
          const contactParts = [entry.contactName, entry.contactEmail, entry.contactPhone].filter(Boolean);
          return (
            <li key={index} className={styles.entry}>
              <div className={styles.marker}>
                <div className={styles.dot} />
                <div className={styles.line} />
              </div>
              <div className={styles.content}>
                <div className={styles.header}>
                  <span className={styles.date}>{formatDate(entry.date)}</span>
                  <span className={styles.dash}>&mdash;</span>
                  <StatusBadge variant={EVENT_BADGE_VARIANT[entry.event]}>{EVENT_LABELS[entry.event]}</StatusBadge>
                </div>
                <div className={styles.detail}>
                  {entry.detail}
                  {entry.source && <span className={styles.source}> via {entry.source}</span>}
                </div>
                {contactParts.length > 0 && <div className={styles.contact}>{contactParts.join(' · ')}</div>}
              </div>
            </li>
          );
        })}
      </ul>

      <ModalActions>
        <Button variant="secondary" onClick={onBack}>
          &lsaquo;&nbsp;&nbsp;Back
        </Button>
      </ModalActions>
    </>
  );
}
